import { Controller } from '@hotwired/stimulus';
import { streamRequest } from '../js/stream_request';

// Formulaire de détendeur de l'adhérent : périphériques
// existants ou nouveaux ajoutés à la liste du formulaire (clé
// `peripheral_form`), retirés (un périphérique enregistré redevient
// disponible) ; le serveur répond en Turbo Streams (ligne, options de la
// liste, « Aucun périphérique », erreur), la liste à recherche est relue à
// chaque option ajoutée ou retirée. `peripheralsUrl` (bureau, création) :
// propriétaire changé, périphériques proposés rechargés et liste vidée. La
// référence locale d'un nouveau périphérique est exigée quand son champ est
// affiché.
export default class extends Controller {
    static targets = ['subform'];

    static values = { addUrl: String, peripheralsUrl: String };

    connect() {
        this.newId = -1;
        this.observer = new MutationObserver((mutations) => {
            if (mutations.some(({ target }) => target.id === 'peripherals')) this.renderChoices();
        });
        this.observer.observe(this.element, { childList: true, subtree: true });
    }

    disconnect() {
        this.observer.disconnect();
    }

    addExisting() {
        const id = this.field('peripherals')?.value;
        if (!id) return;
        streamRequest(this.addUrlValue, { params: { peripheral_form: this.formKey, 'peripheral[id]': id } });
    }

    // Entrée dans le sous-formulaire : ajout du nouveau périphérique, jamais
    // l'envoi du formulaire (Entrée qui choisit dans une liste : rien).
    enter(event) {
        if (event.defaultPrevented) return;
        event.preventDefault();
        this.addNew();
    }

    async addNew() {
        const value = (id) => this.field(`peripheral_${id}`)?.value.trim() ?? '';
        const localRef = this.field('peripheral_local_ref');
        const refRequired = Boolean(localRef) && !localRef.disabled && !localRef.closest('[hidden]');
        const missing = [['brand_id', 'marque'], ['model', 'modèle'], ['purchased_at', 'achat'], ['service_at', 'mise en service']]
            .filter(([id]) => value(id) === '').map(([, label]) => label);
        if (refRequired && value('local_ref') === '') missing.push('réf. locale');
        if (missing.length > 0) {
            this.showError(`À renseigner : ${missing.join(', ')}.`);
            return;
        }
        this.showError('');

        // Identifiant pris avant l'envoi : un double clic n'envoie pas deux fois le même.
        const id = this.newId;
        this.newId -= 1;
        const fields = {
            id, brand_id: value('brand_id'), model: value('model'), peripheral_type: value('peripheral_type'), hose_type: value('hose_type'),
            hose_material: value('hose_material'), max_pressure: value('max_pressure'), hose_length: value('hose_length'),
            purchased_at: value('purchased_at'), service_at: value('service_at'), controlled_at: value('controlled_at'),
            manufacturer_code: value('manufacturer_code'), local_ref: refRequired ? value('local_ref') : '',
        };
        const params = { peripheral_form: this.formKey };
        Object.entries(fields).forEach(([key, field]) => { params[`peripheral[${key}]`] = field; });
        if (!(await streamRequest(this.addUrlValue, { params }))) return;

        // Sous-formulaire vidé (textes, nombres, dates, marque) ; les types
        // gardent leur choix.
        this.subformTarget.querySelectorAll('input[type=text]:not([role=combobox]), input[type=number], input[type=date]')
            .forEach((input) => { input.value = ''; });
        const brand = this.field('peripheral_brand_id');
        brand.value = '';
        brand.dispatchEvent(new CustomEvent('combobox:render', { bubbles: true }));
    }

    ownerChanged({ target }) {
        if (target.name !== 'user_owned' || !this.hasPeripheralsUrlValue) return;
        streamRequest(this.peripheralsUrlValue, { params: { state: target.value === 'true', peripheral_form: this.formKey } });
    }

    remove({ params: { id, url } }) {
        streamRequest(url, { method: 'DELETE', params: { peripheral_id: id, peripheral_form: this.formKey } });
    }

    renderChoices() {
        this.field('peripherals')?.dispatchEvent(new CustomEvent('combobox:render', { bubbles: true }));
    }

    showError(message) {
        const error = this.field('error_peripheral');
        if (!error) return;
        error.textContent = message;
        error.hidden = message === '';
    }

    field(id) {
        return this.element.querySelector(`#${id}`);
    }

    get formKey() {
        return this.field('peripheral_form')?.value ?? '';
    }
}
